import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { Analytics } from '@/lib/analytics';
import { parseGoalsFromText } from '@/utils/goalParser';

export interface Goal {
  id: string;
  goal_type: string;
  exercise_name: string | null;
  target_value: number;
  target_unit: string;
  current_value: number;
  starting_value: number | null;
  target_date: string | null;
  is_active: boolean;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface Milestone {
  id: string;
  goal_id: string;
  milestone_type: string;
  achievement_value: number | null;
  achievement_unit: string | null;
  percentage_achieved: number | null;
  celebration_shown: boolean;
  achieved_at: string;
}

export const useGoals = () => {
  const { user } = useAuth();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchGoals = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { data: goalsData, error: goalsError } = await supabase
        .from('user_goals')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (goalsError) throw goalsError;
      setGoals(goalsData || []);

      // Fetch milestones
      const { data: milestonesData, error: milestonesError } = await supabase
        .from('milestones')
        .select('*')
        .eq('user_id', user.id)
        .eq('celebration_shown', false)
        .order('achieved_at', { ascending: false });

      if (milestonesError) throw milestonesError;
      setMilestones(milestonesData || []);

    } catch (error) {
      console.error('Error fetching goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const createGoalsFromText = async (specificGoalsText: string, maxGoals?: number) => {
    if (!user || !specificGoalsText?.trim()) return [];

    try {
      // Never create duplicates if the user already has active goals
      const { count: existingCount } = await supabase
        .from('user_goals')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('is_active', true);
      if ((existingCount ?? 0) > 0) {
        console.log('[Goals] Skipping createGoalsFromText — active goals already exist');
        return [];
      }

      const parsedGoals = parseGoalsFromText(specificGoalsText);
      const limited =
        typeof maxGoals === 'number' && maxGoals > 0
          ? parsedGoals.slice(0, maxGoals)
          : parsedGoals.slice(0, 1); // default: at most one when seeding from text
      const createdGoals = [];

      for (const parsedGoal of limited) {
        try {
          const { data, error } = await supabase
            .from('user_goals')
            .insert([{
              ...parsedGoal,
              user_id: user.id
            }])
            .select()
            .single();

          if (error) throw error;
          createdGoals.push(data);
        } catch (error) {
          console.error('Error creating individual goal:', error);
        }
      }

      await fetchGoals();
      return createdGoals;
    } catch (error) {
      console.error('Error creating goals from text:', error);
      throw error;
    }
  };

  const createGoal = async (goalData: Omit<Goal, 'id' | 'created_at' | 'updated_at'>) => {
    if (!user) {
      console.error('No user found when trying to create goal');
      throw new Error('You must be logged in to create a goal');
    }

    try {
      // Free plan: enforce max active goals at the write path (covers onboarding + Progress races)
      const wantsActive = goalData.is_active !== false;
      if (wantsActive) {
        const { data: profileRow } = await supabase
          .from('profiles')
          .select('subscription_tier, subscription_expires_at')
          .eq('user_id', user.id)
          .maybeSingle();
        const tier = profileRow?.subscription_tier;
        const expires = profileRow?.subscription_expires_at
          ? new Date(profileRow.subscription_expires_at).getTime()
          : null;
        const isPremium =
          tier === 'premium' && (expires == null || expires > Date.now());
        if (!isPremium) {
          const { count } = await supabase
            .from('user_goals')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .eq('is_active', true);
          if ((count ?? 0) >= 1) {
            throw new Error('Free accounts can have 1 active goal. Upgrade to Premium for more.');
          }
        }
      }

      // Set starting_value to current_value when creating a goal
      const goalWithStartingValue = {
        ...goalData,
        user_id: user.id,
        starting_value: goalData.current_value // Store the starting point
      };
      
      console.log('Inserting goal into database:', goalWithStartingValue);
      
      const { data, error } = await supabase
        .from('user_goals')
        .insert([goalWithStartingValue])
        .select()
        .single();

      if (error) {
        console.error('Database error creating goal:', error);
        throw new Error(error.message || 'Failed to create goal in database');
      }
      
      console.log('Goal created successfully:', data);
      Analytics.goalCreated(goalData.goal_type, goalData.exercise_name || 'General');
      await fetchGoals();
      return data;
    } catch (error: any) {
      console.error('Error creating goal:', error);
      throw error;
    }
  };

  const updateGoalProgress = async (goalId: string, newValue: number) => {
    if (!user) return;

    try {
      // If this goal was created before we started storing starting_value,
      // lock the current value as the starting point on the first progress update.
      const goal = goals.find(g => g.id === goalId);

      const updatePayload: Record<string, any> = { current_value: newValue };
      if (goal && goal.starting_value == null && goal.current_value != null) {
        updatePayload.starting_value = goal.current_value;
      }

      const { error } = await supabase
        .from('user_goals')
        .update(updatePayload)
        .eq('id', goalId)
        .eq('user_id', user.id);

      if (error) throw error;
      
      // Check for milestones
      if (goal) {
        const goalType = goal.goal_type?.toLowerCase();
        const unit = goal.target_unit?.toLowerCase() || '';
        const name = goal.exercise_name?.toLowerCase() || '';
        
        // Determine if this is a "lower is better" goal
        const isLowerBetter = 
          goalType === 'running' ||
          (goalType === 'endurance' && (unit === 'minutes' || unit === 'min' || unit === 'seconds' || unit === 'sec')) ||
          goalType === 'weight' && goal.target_value < goal.current_value || // Weight loss
          name.includes('body fat');
        
        const isImprovement = isLowerBetter 
          ? newValue < goal.current_value 
          : newValue > goal.current_value;
          
        if (isImprovement) {
          // Create milestone entry to trigger milestone function
          const { error: milestoneError } = await supabase
            .from('milestones')
            .insert([{
              user_id: user.id,
              goal_id: goalId,
              milestone_type: 'personal_best',
              achievement_value: newValue,
              achievement_unit: goal.target_unit
            }]);

          if (milestoneError && !milestoneError.message.includes('duplicate')) {
            console.error('Error creating milestone:', milestoneError);
          } else if (!milestoneError) {
            Analytics.milestoneReached('personal_best', goal.goal_type);
          }
        }
        
        // Check if goal is achieved
        const isGoalAchieved = isLowerBetter 
          ? newValue <= goal.target_value 
          : newValue >= goal.target_value;
          
        if (isGoalAchieved) {
          Analytics.goalAchieved(goal.goal_type, 0); // daysToAchieve would need to be calculated
        }
        
        Analytics.goalUpdated(goal.goal_type, goal.exercise_name || 'General');
      }
      
      await fetchGoals();
    } catch (error) {
      console.error('Error updating goal progress:', error);
      throw error;
    }
  };

  const markMilestoneAsShown = async (milestoneId: string) => {
    try {
      const { error } = await supabase
        .from('milestones')
        .update({ celebration_shown: true })
        .eq('id', milestoneId);

      if (error) throw error;
      
      setMilestones(prev => prev.filter(m => m.id !== milestoneId));
    } catch (error) {
      console.error('Error marking milestone as shown:', error);
    }
  };

  const deleteGoal = async (goalId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_goals')
        .delete()
        .eq('id', goalId)
        .eq('user_id', user.id);

      if (error) throw error;
      
      await fetchGoals();
    } catch (error) {
      console.error('Error deleting goal:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (user) {
      fetchGoals();
    }
  }, [user]);

  return {
    goals,
    milestones,
    loading,
    createGoal,
    createGoalsFromText,
    updateGoalProgress,
    deleteGoal,
    markMilestoneAsShown,
    refetch: fetchGoals
  };
};